import { Injectable } from '@angular/core';
import { Globals } from '@smarttask-common/src/lib/services/globals';
import * as prismic from '@prismicio/client';
import { asHTML } from '@prismicio/helpers';

@Injectable({
    providedIn: 'root',
})
export class PrismicService {
    private prismic_client: prismic.Client;

    constructor(private globals: Globals) {
        this.prismic_client = prismic.createClient(this.globals.prismic.api_url, {
            accessToken: this.globals.prismic.access_token,
        });
    }

    getClient(): prismic.Client {
        return this.prismic_client;
    }

    getHtml = (text) => {
        const htmlSerializer = this.htmlSerializer();
        let html = asHTML(text, this.linkResolver, htmlSerializer);
        return html;
    };

    getImageUrlWithoutBaseUrl = (url: string): string => {
        let to_ret = url;
        if (url.startsWith('https://images.prismic.io/smarttask/')) {
            to_ret = url.replace('https://images.prismic.io/smarttask/', '');
        }
        return to_ret;
    };

    htmlSerializer =
        (ids_used = {}) =>
        (type, element, content, children) => {
            switch (type) {
                // Add a class to paragraph elements
                case prismic.RichTextNodeType.paragraph:
                    return '<p class="paragraph">' + children + '</p>';

                // Don't wrap images in a <p> tag
                case prismic.RichTextNodeType.image:
                    return `<img class="img-fluid st-img-crisp mb-4 d-flex align-items-center"  src="${element.url}?w=1600&fit=max&auto=compress,format" 
                        srcset="
                            ${element.url}?w=400&fit=max&auto=compress,format 400w, 
                            ${element.url}?w=800&fit=max&auto=compress,format 800w,
                            ${element.url}?w=1600&fit=max&auto=compress,format 1600w
                        " alt="${element.alt}" loading="lazy">`;

                case prismic.RichTextNodeType.embed:
                    return (
                        "<div class='embed pt-2 pb-3 d-flex align-items-center justify-content-center' data-oembed='" +
                        element.oembed.embed_url +
                        "' data-oembed-type='" +
                        element.oembed.type +
                        "' data-oembed-provider='" +
                        element.oembed.provider_name +
                        "'>" +
                        element.oembed.html +
                        '</div>'
                    );

                case prismic.RichTextNodeType.heading1: {
                    const text = this.getIdFromRichTextElement(element, ids_used);
                    return `<h1 id="${text}">${children}</h1>`;
                }
                case prismic.RichTextNodeType.heading2: {
                    const text = this.getIdFromRichTextElement(element, ids_used);
                    return `<h2 id="${text}">${children}</h2>`;
                }
                case prismic.RichTextNodeType.heading3: {
                    const text = this.getIdFromRichTextElement(element, ids_used);
                    return `<h3 id="${text}">${children}</h3>`;
                }
                case prismic.RichTextNodeType.heading4: {
                    const text = this.getIdFromRichTextElement(element, ids_used);
                    return `<h4 id="${text}">${children}</h4>`;
                }
                case prismic.RichTextNodeType.heading5: {
                    const text = this.getIdFromRichTextElement(element, ids_used);
                    return `<h5 id="${text}">${children}</h5>`;
                }
                case prismic.RichTextNodeType.heading6: {
                    const text = this.getIdFromRichTextElement(element, ids_used);
                    return `<h6 id="${text}">${children}</h6>`;
                }

                // Return null to stick with the default behavior
                default:
                    return null;
            }
        };

    getIdFromRichTextElement = (element: any, ids_used): string => {
        const text =
            'st-' +
            element.text
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9 ]/g, '') // Remove all special characters except spaces
                .replace(/\s+/g, '-');
        const pos = ids_used[text] ? ids_used[text] + 1 : 1;
        ids_used[text] = pos;
        return `${text}-${pos}`;
    };

    linkResolver = (doc) => {
        // Pretty URLs for known types
        if (doc.type === 'blog_posts') return '/blog/' + doc.uid;
        if (doc.type === 'page') return '/' + doc.uid;
        // Fallback for other types, in case new custom types get created
        return '/doc/' + doc.id;
    };

    toUpper = (str: string) => {
        return str
            .toLowerCase()
            .split(' ')
            .map(function (word) {
                //console.log("First capital letter: "+word[0]);
                //console.log("remain letters: "+ word.substr(1));
                return word[0].toUpperCase() + word.substr(1);
            })
            .join(' ');
    };
    getStringFromUrl = (str: string) => {
        str = str == undefined ? '' : str;
        str = str.replace('-', ' ');
        str = this.toUpper(str);
        return str;
    };
    getStringForUrl = (str: string) => {
        str = str == undefined ? '' : str;
        str = str.toLowerCase();
        str = str.replace(' ', '-');
        return str;
    };
    getImgSrcSet = (img_url: string): string => {
        if (!img_url) return '';

        const widths = [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840];

        let uri = new URL(img_url);
        let img_url_without_quotes = uri.origin + uri.pathname;

        return widths.map((w) => `${img_url_without_quotes}?auto=format,compress&w=${w} ${w}w`).join(', ');
    };
}
